import { isArray } from "lodash"
import { Requests } from "./Requests"

export default class OrganizationAPI {

  /**
   * Search organizations server-side by case-insensitive name match, one page at
   * a time. Replaces fetching every org and filtering client-side, which silently
   * dropped orgs beyond the server's result cap (HMB-18829).
   * @param {string} endpoint
   * @param {string} [search] - name search text; empty paginates over all orgs
   * @param {{ limit?: number, offset?: number }} [opts] - server clamps limit to 100
   * @returns {Promise<{ data: Array<Object>, pagination: { total: number, limit: number, offset: number, hasMore: boolean } }>}
   */
  static async fetchOrganizationsBySearch(endpoint, search = '', { limit, offset } = {}){
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (limit) params.set('limit', limit)
    if (offset) params.set('offset', offset)
    const queryString = params.toString()
    const url = `${endpoint}/api/toolkit/organization${queryString ? `?${queryString}` : ''}`
    const response = await Requests.authRequest(url, 'GET')
    // Tolerate a legacy bare-array response as well as the { data, pagination }
    // envelope so the dropdown never empties if an env predates the paginated API.
    const data = isArray(response) ? response : (isArray(response?.data) ? response.data : [])
    const pagination = response?.pagination || { total: data.length, limit: data.length, offset: 0, hasMore: false }
    return { data, pagination }
  }

  /**
   * Fetch an organization by id
   * @param {Number|String} id
   * @returns {Object}
   */
  static async fetchOrganization(endpoint, id){
    const url = `${endpoint}/api/organization/${id}`
    return Requests.authRequest(url, "GET")
  }

  /**
   * Creates an organization using the API directly
   * @param {Object} orgData - Organization data object
   * @param {string} orgData.organizationName - Name of the organization
   * @param {string} orgData.identityProvider - Authentication type (MARLEY or FEDERATED)
   * @param {string} [orgData.domainName] - Domain name for FEDERATED auth
   * @param {boolean} [orgData.createSupportUser=false] - Whether to create a support user for the organization
   * @param {Object} [orgData.integration] - Integration configuration
   * @param {string} [orgData.integration.authentication] - Authentication method (KEY or OAUTH2)
   * @returns {Promise<Object>} The created organization object
   */
  static async createOrganizationRequest(body, endpoint) {
    const url = `${endpoint}/api/toolkit/organization`;
    return Requests.authRequest(url, 'POST', body, true);
  }

  // Utility function to extract and normalize domain from email
  static getNormalizedDomainMarleyUser = (input) => {
    if (!input || typeof input !== 'string') return null;
    
    // extract the domain
    let domain = input.includes('@') ? input.split('@')[1] : input;
    
    // Normalize the domain (lowercase, remove www)
    return domain.toLowerCase().replace(/^www\./, '');
  }
}